import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { openBookingModal } from "@/components/arqera/ArqBookingModal";

export default function ArqHero() {
  const [activeRow, setActiveRow] = useState(0);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const id = setInterval(() => setActiveRow((i) => (i + 1) % 4), 2200);
    return () => clearInterval(id);
  }, []);
  return (
    <section className="arq-radial-tl relative min-h-screen flex items-center pt-32 md:pt-32 pb-20 px-6 overflow-hidden">
      {/* Subtle right-side grid */}
      <div
        aria-hidden
        className="hidden md:block absolute right-0 top-0 bottom-0 w-[45%] opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(hsl(var(--arq-blue)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--arq-blue)) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse at 70% 50%, black 30%, transparent 75%)",
        }}
      />

      <div className="max-w-[1200px] mx-auto w-full relative z-10">
        <div className="grid md:grid-cols-[55%_40%] md:gap-[5%] items-center">
          <div>
            <div
              className="arq-label mb-6 opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
              style={{ animationDelay: "0ms" }}
            >
              AI Feature Security for Regulated Teams
            </div>

            <h1
              className="font-display font-bold text-arq-text leading-[1.1] tracking-[-0.02em] mb-6 opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
              style={{ fontSize: "clamp(40px, 6.5vw, 64px)", animationDelay: "0ms" }}
            >
              Ship AI Features <span className="text-arq-blue">Safely.</span>
            </h1>

            <p
              className="text-arq-muted mb-4 max-w-[580px] opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
              style={{ fontSize: "clamp(17px, 1.6vw, 20px)", lineHeight: 1.6, animationDelay: "150ms" }}
            >
              ArqeraAI helps software and regulated teams find, fix, and validate AI feature
              risks before they expose customer data, break compliance, or stall enterprise deals.
            </p>
            <p
              className="text-arq-tertiary mb-6 max-w-[580px] text-[14.5px] leading-[1.65] opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
              style={{ animationDelay: "180ms" }}
            >
              We review the application-layer risks around data access, authorization, prompt
              injection, RAG exposure, logging, privacy, and compliance evidence.
            </p>

            <div
              className="flex flex-wrap gap-2 mt-2 mb-2 opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
              style={{ animationDelay: "200ms" }}
            >
              {["OWASP LLM", "MITRE ATLAS", "NIST AI RMF", "HIPAA", "SOC 2", "CMMC", "HITRUST"].map(f => (
                <span key={f} className="arq-tag font-mono text-[11px] text-arq-teal border-arq-teal/40">{f}</span>
              ))}
            </div>

            <a
              href="/resources"
              className="text-[13px] text-arq-teal underline underline-offset-2 hover:text-arq-blue transition-colors mb-4 inline-block opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
              style={{ animationDelay: "250ms" }}
            >
              Download the AI Feature Launch Risk Checklist →
            </a>

            <div
              className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6 opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
              style={{ animationDelay: "300ms" }}
            >
              <Link
                to="/ai-feature-security-review"
                className="bg-arq-blue text-white font-semibold text-[15px] px-8 py-4 rounded-md hover:bg-arq-blue-bright hover:shadow-[0_0_24px_rgba(26,111,212,0.35)] transition-all duration-150 tracking-[0.01em] text-center"
              >
                Book an AI Feature Security Review
              </Link>
              <button
                onClick={() => document.querySelector("#ai-feature-security-review")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-transparent text-arq-text font-medium text-[15px] px-2 py-4 hover:underline underline-offset-4 transition-all duration-150"
              >
                See What We Review
              </button>
            </div>


            <div
              className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[13px] text-arq-tertiary opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards] mb-3"
              style={{ animationDelay: "400ms" }}
            >
              <span className="text-arq-muted">Not ready to talk yet?</span>
              <a href="/ai-transformation-audit" className="text-arq-teal underline underline-offset-2 hover:text-arq-blue transition-colors">
                Take the 3-minute AI Security Audit
              </a>
              <span className="text-arq-tertiary">·</span>
              <a href="/quality-crystal-ball" className="text-arq-teal underline underline-offset-2 hover:text-arq-blue transition-colors">
                Try the Quality Crystal Ball demo
              </a>
            </div>

            <div
              className="flex flex-wrap items-center gap-x-2 gap-y-2 text-[13px] text-arq-tertiary opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
              style={{ animationDelay: "450ms" }}
            >
              <span>10-business-day focused review</span>
              <span>·</span>
              <span>Executive and technical findings</span>
              <span>·</span>
              <span>Remediation roadmap</span>
              <span>·</span>
              <span>Evidence-ready deliverables</span>
            </div>

          </div>

          {/* Right column - AI Risk Report card mock (md+) */}
          <div
            className="hidden md:block opacity-0 animate-[fade-in_600ms_cubic-bezier(0.16,1,0.32,1)_forwards]"
            style={{ animationDelay: "450ms" }}
          >
            <div className="relative bg-arq-surface1 border border-arq-border rounded-xl p-6 shadow-[0_8px_40px_rgba(0,0,0,0.4)] overflow-hidden">
              {/* Scan line */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 h-px bg-gradient-to-r from-transparent via-arq-blue/60 to-transparent motion-safe:animate-[arq-scan_4.5s_linear_infinite]"
                style={{ top: 0 }}
              />
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.12em] uppercase text-arq-muted">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inset-0 rounded-full bg-arq-teal motion-safe:animate-ping opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-arq-teal" />
                  </span>
                  AI Feature Security Review
                </div>
                <div className="font-mono text-[10px] text-arq-tertiary tracking-[0.08em]">
                  REV-████
                </div>
              </div>

              <div className="space-y-2.5">
                {[
                  {
                    id: "TEN-ISO",
                    sev: "AT RISK",
                    sevColor: "#D97706",
                    title: "Tenant Isolation",
                    meta: "Cross-tenant data access path observed",
                  },
                  {
                    id: "LLM01",
                    sev: "FOUND",
                    sevColor: "#C8402A",
                    title: "Prompt Injection Path",
                    meta: "OWASP LLM01 · Tool-use exfiltration",
                  },
                  {
                    id: "PRV-LOG",
                    sev: "REVIEW",
                    sevColor: "hsl(var(--arq-teal))",
                    title: "Sensitive Data Logging",
                    meta: "PII written to model trace logs",
                  },
                  {
                    id: "LNCH",
                    sev: "3 CRITICAL",
                    sevColor: "#C8402A",
                    title: "Launch Readiness",
                    meta: "3 critical gaps before customer rollout",
                  },
                ].map((row, i) => {
                  const isActive = activeRow === i;
                  return (
                    <div
                      key={row.id}
                      className={`p-3 rounded-md bg-arq-bg/60 border transition-all duration-500 ${
                        isActive
                          ? "border-arq-blue/60 shadow-[0_0_0_1px_hsl(var(--arq-blue)/0.25),0_0_24px_-4px_hsl(var(--arq-blue)/0.45)] translate-x-[2px]"
                          : "border-arq-border"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span
                          className="font-mono text-[10px] tracking-[0.08em] px-1.5 py-0.5 rounded"
                          style={{
                            color: row.sevColor,
                            background: `${row.sevColor}1A`,
                            border: `1px solid ${row.sevColor}40`,
                          }}
                        >
                          {row.sev}
                        </span>
                        <span className="font-mono text-[10px] text-arq-tertiary tracking-[0.06em]">
                          {row.id}
                        </span>
                      </div>
                      <div className="text-arq-text text-[13.5px] leading-tight mb-1">
                        {row.title}
                      </div>
                      <div className="font-mono text-[10.5px] text-arq-muted tracking-[0.03em]">
                        {row.meta}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 pt-3 border-t border-arq-border font-mono text-[10.5px] text-arq-tertiary tracking-[0.05em] flex items-center justify-between">
                <span>ArqeraAI Review · Executive + technical remediation roadmap</span>
                <span className="text-arq-teal motion-safe:animate-pulse">●</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
